import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { SnackbarProvider } from 'notistack';
import { Navigate, Route, Routes } from 'react-router-dom';
import { pdfjs } from 'react-pdf';
import { AppThemeProvider } from './components/AppThemeProvider';
import { ThemeContextProvider, useAppTheme } from './contexts/ThemeContext';
import CoursePage from './pages/Course';
import CourseDetailPage from './pages/CourseDetail';
import EditCoursePage from './pages/EditCoursePage';
import CourseIdRedirect from './components/CourseIdRedirect';
import SignInPage from './pages/SignIn';
import AcceptInvitePage from './pages/AcceptInvitePage';
import StudentsPage from './pages/Students';
import WorkspacesPage from './pages/Workspaces';
import WorkspaceEditPage from './pages/WorkspaceEdit';
import GroupEditPage from './pages/GroupEdit';
import AssignmentsPage from './pages/Assignments';
import ProfilePage from './pages/Profile';
import TagsPage from './pages/Tags';
import GroupsPage from './pages/Groups';
import Notifications from './pages/Notifications';
import CertificatesPage from './pages/Certificates';
import SettingsPage from './pages/Settings';
import CustomFieldsPage from './pages/CustomFieldsPage';
import CourseInstructorsPage from './pages/CourseInstructors';
import ReportsPage from './pages/Reports';
import ProtectedRoute from './routes/ProtectedRoute';
import LayoutRoute from './routes/LayoutRoute';
import { LayoutProvider } from './contexts/LayoutContext';
import { OwnedGroupsProvider } from './contexts/OwnedGroupsContext';


pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js`;

/**
 * Inner App component that has access to theme context
 */
function ThemedApp() {
  const { theme } = useAppTheme();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        <main className="flex-grow">
          <SnackbarProvider maxSnack={3}>
            <LayoutProvider>
              <OwnedGroupsProvider>
                <Routes>
                {/* Public routes (no auth required) */}
                <Route path="/signin" element={<SignInPage />} />
                <Route path="/accept-invite" element={<AcceptInvitePage />} />

                {/* Legacy course URL redirect - fetches slug and redirects */}
                <Route path="/course/:id" element={<CourseIdRedirect />} />

                {/* All routes that share the main layout (persistent NavBar + WorkspaceSwitcher) */}
                <Route element={<LayoutRoute />}>
                  {/* Course routes - accessible to all, conditional rendering based on auth */}
                  <Route path="/courses" element={<CoursePage />} />
                  <Route path="/courses/:slug" element={<CourseDetailPage />} />

                  {/* Protected routes */}
                  <Route element={<ProtectedRoute />}>
                    {/* Admin course editor — full page, replaces the old modal.
                        `/new` must precede the `:slug` route in case of v6
                        ranking edge-cases, but both should resolve correctly. */}
                    <Route path="/courses/new" element={<EditCoursePage />} />
                    <Route path="/courses/:slug/edit" element={<EditCoursePage />} />
                    <Route path="/assignments" element={<AssignmentsPage />} />
                    <Route path="/students" element={<StudentsPage />} />
                    <Route path="/students/:id" element={<ProfilePage />} />
                    <Route path="/tags" element={<TagsPage />} />
                    <Route path="/groups" element={<GroupsPage />} />
                    <Route path="/workspaces" element={<WorkspacesPage />} />
                    <Route path="/workspaces/:id" element={<WorkspaceEditPage />} />
                    <Route path="/workspaces/:wsId/groups/:groupId" element={<GroupEditPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/certificates" element={<CertificatesPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/settings/custom-fields" element={<CustomFieldsPage />} />
                    <Route path="/settings/instructors" element={<CourseInstructorsPage />} />
                    <Route path="/reports" element={<ReportsPage />} />
                    <Route path="/certificate-template" element={<Navigate to="/settings" replace />} />
                  </Route>
                </Route>

                {/* Default redirect to courses */}
                <Route path="*" element={<Navigate to="/courses" replace />} />
                </Routes>
              </OwnedGroupsProvider>
            </LayoutProvider>
          </SnackbarProvider>
        </main>
      </div>
    </ThemeProvider>
  );
}

/**
 * Main App component with theme context providers
 *
 * Provider hierarchy:
 * 1. ThemeContextProvider - Manages theme state
 * 2. AppThemeProvider - Fetches tenant config and updates theme
 * 3. ThemeProvider (MUI) - Applies theme to MUI components
 */
function App() {
  return (
    <ThemeContextProvider>
      <AppThemeProvider>
        <ThemedApp />
      </AppThemeProvider>
    </ThemeContextProvider>
  );
}

export default App;
